// Fills the browser cache (cache.ts) with everything, in the background: every
// level of every chunk of every cloud (shown ones first), then whole files the
// editor would load (each scan's orig/ build). It only fetches while the viewer
// has nothing in flight and the page is visible, one range at a time, and not
// at all when the browser asks to save data.
import type { Viewer } from './viewer.ts';
import * as cache from './cache.ts';

const RUN = 4 << 20;   // bytes per request

export class Prefetcher {
  total = 0;           // bytes of everything it wants kept
  have = 0;            // of those, kept now
  state: 'idle' | 'checking' | 'fetching' | 'done' | 'off' = 'idle';
  error = '';
  private stop = false;

  constructor(private v: Viewer, private files: () => Promise<string[]>) {}

  async run() {
    if (!(await cache.available())) { this.state = 'off'; this.error = 'no browser cache here'; return; }
    if ((navigator as { connection?: { saveData?: boolean } }).connection?.saveData) { this.state = 'off'; this.error = 'the browser asks to save data'; return; }
    this.state = 'checking';
    const kept = await cache.keys();
    type Job = { url: string; parts: cache.Part[] } | { url: string; whole: true };
    const jobs: Job[] = [];
    // clouds: shown ones first; each chunk level is one slice, in file order, in runs of up to RUN bytes
    const clouds = [...this.v.clouds].sort((a, b) => +b.visible - +a.visible);
    for (const c of clouds) {
      const parts: cache.Part[] = [];
      for (const ch of c.chunks) ch.info.levelCounts.forEach((n, l) => { if (n) parts.push({ offset: ch.info.offsets[l], bytes: n * c.idx.record }); });
      parts.sort((a, b) => a.offset - b.offset);
      let run: cache.Part[] = [], size = 0;
      const flush = () => { if (run.length) jobs.push({ url: c.bin, parts: run }); run = []; size = 0; };
      for (const p of parts) {
        this.total += p.bytes;
        if (kept.has(cache.abs(cache.sliceKey(c.bin, p.offset, p.bytes)))) { this.have += p.bytes; continue; }
        const last = run[run.length - 1];
        if (last && (last.offset + last.bytes !== p.offset || size + p.bytes > RUN)) flush();
        run.push(p); size += p.bytes;
      }
      flush();
    }
    for (const url of await this.files().catch(() => [] as string[])) {
      const n = await sizeOf(url);
      this.total += n;
      if (kept.has(cache.abs(url))) this.have += n; else jobs.push({ url, whole: true });
    }
    if (this.stop) return;
    this.state = jobs.length ? 'fetching' : 'done';
    for (const j of jobs) {
      while (!this.stop && (this.v.inFlight > 0 || document.hidden)) await sleep(500);   // the view comes first
      if (this.stop) return;
      try {
        if ('whole' in j) { const b = await cache.fetchBuf(j.url); this.have += b.byteLength; } else {
          await cache.fetchParts(j.url, j.parts);
          this.have += j.parts.reduce((a, p) => a + p.bytes, 0);
        }
      } catch (e) { this.error = String((e as Error).message ?? e); }
      await sleep(50);
    }
    this.state = 'done';
  }

  cancel() {
    this.stop = true;
    if (this.state === 'checking' || this.state === 'fetching') { this.state = 'off'; this.error = 'stopped'; }
  }
}

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

/** A file's size without fetching it (HEAD), 0 if unknown. */
async function sizeOf(url: string) {
  try { const r = await fetch(url, { method: 'HEAD' }); return +(r.headers.get('content-length') ?? 0); } catch { return 0; }
}
